import { Capacitor, registerPlugin } from '@capacitor/core';
import { toast } from 'react-toastify';

const PdfDownload = registerPlugin('PdfDownload');

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const triggerWebDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const openNativePdf = async (path) => {
  try {
    const result = await PdfDownload.openPdf({ path });
    if (!result?.opened) {
      toast.info('The PDF is saved on this device, but no PDF viewer is available.');
    }
  } catch (error) {
    console.error('[savePdfBlob] Could not open PDF:', error);
    toast.error('The PDF was saved, but could not be opened.');
  }
};

export const savePdfBlob = async (blob, filename) => {
  const safeFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;

  if (!Capacitor.isNativePlatform()) {
    triggerWebDownload(blob, safeFilename);
    return { success: true, filename: safeFilename };
  }

  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const base64Data = await blobToBase64(blob);
    const writeResult = await Filesystem.writeFile({
      path: safeFilename,
      data: base64Data,
      directory: Directory.Data,
      recursive: true,
    });

    const savedFile = {
      success: true,
      filename: safeFilename,
      path: writeResult.uri,
      open: () => openNativePdf(writeResult.uri),
    };

    try {
      await PdfDownload.showDownloadNotification({
        path: writeResult.uri,
        title: 'Download complete',
        body: `${safeFilename} is ready. Tap to open it.`,
      });
    } catch (notificationError) {
      console.warn('[savePdfBlob] Download notification was not shown:', notificationError);
    }

    toast.success(`${safeFilename} downloaded. Tap to open.`, {
      onClick: savedFile.open,
      autoClose: 5000,
    });
    return savedFile;
  } catch (err) {
    console.error('[savePdfBlob] Failed:', err);
    toast.error('Unable to save PDF. Please try again.');
    return false;
  }
};
